import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

/**
 * Badge semantics (deliberate decision):
 * Count of current user's `pending` offers in the current org.
 *
 * Query key is org- and user-scoped to avoid cache bleed across contexts.
 */

function badgeQueryKey(orgId: string | null, userId: string | undefined) {
  return ['my-offers-badge-count', orgId, userId] as const;
}

export function useMyOffersBadgeCount() {
  const { user, currentOrgId } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();

  const queryKey = badgeQueryKey(currentOrgId, user?.id);

  const { data: count = 0 } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!user || !currentOrgId) return 0;
      const { count } = await supabase
        .from('offers')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('org_id', currentOrgId)
        .eq('status', 'pending');
      return count ?? 0;
    },
    enabled: !!user && !!currentOrgId,
    staleTime: 30_000,
    refetchOnWindowFocus: false, // we handle visibility explicitly below
  });

  // Refetch on route navigation
  useEffect(() => {
    if (user && currentOrgId) {
      queryClient.invalidateQueries({ queryKey });
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch on tab becoming visible
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible' && user && currentOrgId) {
        queryClient.invalidateQueries({ queryKey });
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [user?.id, currentOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { count, queryKey };
}

/** Exported for use in mutation sites (e.g. Offers page accept/decline) */
export { badgeQueryKey };
